import React, { useState } from 'react'
import { t } from '../../../i18n/i18n'
import { getCostEvaluationColumns, getCostEvaluationGroups, getCostEvaluationSummaryFields } from '../../../helpers/riskEconomyHelper'
import { ActionPlanStatus, Cost, FinancialYear, FinancialYearStatus } from '../../../api/schemas/schema'
import { IItemState } from '../../../interfaces/IItemState'
import { CostEvaluationList } from './CostEvaluationList'
import { CostEvaluationPhoneHeader } from './CostEvaluationPhoneHeader'
import { CostEvaluationPhoneRow } from './CostEvaluationPhoneRow'
import { CardHeader } from '../../common/header/CardHeader'
import { ModalEvaluateCost } from './ModalEvaluateCost'
import { makeStyles, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components'

const useStyles = makeStyles({
   wrapText: {
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
   },
})

type CostEvaluationProps = {
   financialYearState: IItemState<FinancialYear>
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
}

export const CostEvaluation = ({ financialYearState, setFinancialYearState }: CostEvaluationProps) => {
   const classes = useStyles()
   const [openModal, setOpenModal] = useState<boolean>(false)
   const [checkboxHasLocalState, setCheckboxHasLocalState] = useState<boolean>(false)
   const [selectedCost, setSelectedCost] = useState<Cost>(null)
   const [manualIsDirty, setManualIsDirty] = useState<boolean>(false)
   const financialYear = financialYearState.item

   const toggleCheckbox = (cost: Cost, openModal?: boolean) => {
      cost = { ...cost, actionRequired: !cost.actionRequired }

      const updatedCosts = financialYear.costs.map((item) => (item.id === cost.id ? cost : item))
      const updatedFinancialYear = { ...financialYear, costs: updatedCosts }

      setFinancialYearState((prev) => ({
         ...prev,
         item: updatedFinancialYear,
      }))

      openModal && setCheckboxHasLocalState(true)
      return cost
   }

   const onCellClick = (cost: Cost, columnKey: keyof Cost) => {
      if (columnKey === 'actionRequired') {
         cost = toggleCheckbox(cost, true)
         setManualIsDirty(true)
      }

      setSelectedCost(cost)
      setOpenModal(true)
   }

   return (
      <>
         <CardHeader title={t('Evaluation')} />

         {financialYear.costs.length === 0 ? (
            <>
               <MessageBar intent={'info'}>
                  <MessageBarBody>
                     <MessageBarTitle className={classes.wrapText}>{t('AddACostToEvaluate')}</MessageBarTitle>
                  </MessageBarBody>
               </MessageBar>
            </>
         ) : (
            <CostEvaluationList
               columns={getCostEvaluationColumns(financialYearState.disabled, financialYear)}
               groups={getCostEvaluationGroups(financialYear.costs)}
               summaryFields={getCostEvaluationSummaryFields(financialYear.costs)}
               renderPhoneRow={(cost) => <CostEvaluationPhoneRow cost={cost} onCellClick={onCellClick} disabled={financialYearState.disabled} />}
               onCellClick={onCellClick}
               renderPhoneHeader={(costGroup) => <CostEvaluationPhoneHeader costGroup={costGroup} />}
            />
         )}
         <ModalEvaluateCost
            setFinancialYearState={setFinancialYearState}
            financialYearState={financialYearState}
            cost={selectedCost}
            isOpen={openModal}
            close={() => {
               setOpenModal(false)
               setCheckboxHasLocalState(false)
               setManualIsDirty(false)
            }}
            checkboxHasLocalState={checkboxHasLocalState}
            toggleCheckbox={toggleCheckbox}
            disabled={
               financialYearState.disabled ||
               financialYear.status === FinancialYearStatus.Completed ||
               (selectedCost && financialYear.actionPlans.some((ap) => ap.costId === selectedCost.id && ap.status !== ActionPlanStatus.NotDecided))
            }
            manualIsDirty={manualIsDirty}
         />
      </>
   )
}
